import { useState, useMemo, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  IoChevronUpOutline,
  IoChevronDownOutline,
  IoCloseOutline,
  IoInformationCircleOutline,
  IoCallOutline,
  IoLocationOutline,
  IoCalendarOutline,
  IoHomeOutline,
} from 'react-icons/io5';
import { PARTNER_SIGNUP_CATEGORIES, AREA_SIZES, MOVING_TYPES } from '../../../config/ieumConfig';
import {
  INTERNET_SERVICE_TYPES,
  INTERNET_LABELS,
  MOVING_TYPE_LABELS,
  CLEANING_TYPES,
  INTERIOR_STYLES,
} from '../../../services/customerFormService';
import { useAuth } from '../../../context/AuthContext';
import { getMarketDBs, purchaseDB, getPartnerKeywords, addPartnerKeyword, removePartnerKeyword, hasOverdueReceivable } from '../../../services/partnerDashboardService';
import { requestTossPayment, buildOrderId } from '../../../services/tossPaymentService';
import InfoTooltip from '../../../components/common/InfoTooltip';
import AlertModal from '../../../components/common/AlertModal';

// ───── 상수 ─────
const REGIONS = ['서울', '경기', '인천', '부산', '대구', '대전', '광주', '울산', '세종'];

const SORT_CHIPS = [
  { value: 'urgent', label: '긴급순' },
  { value: 'latest', label: '최신순' },
];

const PAYMENT_METHODS = [
  { value: 'transfer', label: '이체' },
  { value: 'card', label: '카드', disabled: true, badge: '준비 중' },
];


const fmt = (n) => `₩${(n || 0).toLocaleString()}`;

// ───── 컴포넌트 ─────
const MarketTab = () => {
  const { userData } = useAuth();
  const [dbs, setDbs] = useState([]);

  const load = useCallback(async () => {
    try {
      const cats = userData?.service_categories || userData?.serviceCategories || null;
      console.log('[MarketTab] 진단 · userData.uid=', userData?.uid, '· service_categories=', cats);
      const list = await getMarketDBs(cats);
      console.log('[MarketTab] getMarketDBs 결과 개수:', list.length, list);
      setDbs(list);
    } catch (err) {
      console.error('market load failed', err);
    }
  }, [userData?.service_categories, userData?.serviceCategories, userData?.uid]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      if (!userData?.uid) return;
      try {
        const list = await getPartnerKeywords(userData.uid);
        setKeywords(list);
      } catch (err) { console.error('keywords load failed', err); }
    })();
  }, [userData?.uid]);
  const [keywordOpen, setKeywordOpen] = useState(true);
  const [filterOpen, setFilterOpen] = useState(true);
  const [sort, setSort] = useState('urgent');
  const [filters, setFilters] = useState({
    category: '',
    region: '',
    areaSize: '',
    movingType: '',
    internetType: '',
    movingDates: [],
    search: '',
  });
  const myCategories = userData?.service_categories || [];
  const showKeywordSection = myCategories.length >= 2;

  const resetFilters = () => setFilters({
    category: '',
    region: '',
    areaSize: '',
    movingType: '',
    internetType: '',
    movingDates: [],
    search: '',
  });

  const hasSubFilters = filters.category === 'moving' || filters.category === 'cleaning';
  const hasProductFilter = filters.category === 'moving' || filters.category === 'internet_tv';

  const toggleMovingDate = (date) =>
    setFilters((prev) => ({
      ...prev,
      movingDates: prev.movingDates.includes(date)
        ? prev.movingDates.filter((d) => d !== date)
        : [...prev.movingDates, date],
    }));

  // 날짜 칩 — 오늘 포함 14일 + DB에 실제 존재하는 일자(과거 포함) 합집합 → 정렬
  const dateChips = useMemo(() => {
    const set = new Set();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 14; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      set.add(d.toISOString().slice(0, 10));
    }
    dbs.forEach((d) => { if (d.movingDate) set.add(d.movingDate); });
    return Array.from(set)
      .filter(Boolean)
      .sort()
      .map((iso) => {
        const d = new Date(iso);
        return { iso, label: Number.isNaN(d.getTime()) ? iso : `${d.getMonth() + 1}/${d.getDate()}` };
      });
  }, [dbs]);
  const [selected, setSelected] = useState([]);
  const [purchaseTarget, setPurchaseTarget] = useState(null);
  const [feeInfoCat, setFeeInfoCat] = useState(null); // 카드별 카테고리
  const [alertMsg, setAlertMsg] = useState('');

  // 키워드 폼
  const [newKwCat, setNewKwCat] = useState('');
  const [newKwRegion, setNewKwRegion] = useState('');
  const [newKwArea, setNewKwArea] = useState('');
  const [newKwDateFrom, setNewKwDateFrom] = useState('');
  const [newKwPriceFrom, setNewKwPriceFrom] = useState('');
  const [newKwPriceTo, setNewKwPriceTo] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [freeTarget, setFreeTarget] = useState(null);
  const [freeDone, setFreeDone] = useState(false);

  const setFilter = (key) => (e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }));

  const toggleSelect = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const addKeyword = async () => {
    if (!newKwCat || !newKwRegion) {
      setAlertMsg('카테고리와 지역은 필수입니다.');
      return;
    }
    if (!userData?.uid) return;
    try {
      const extra = {
        areaSize: newKwArea || '',
        movingDateFrom: newKwDateFrom || '',
        priceFrom: newKwPriceFrom || '',
        priceTo: newKwPriceTo || '',
      };
      const id = await addPartnerKeyword(userData.uid, newKwCat, newKwRegion, extra);
      setKeywords((prev) => [...prev, { id, partner_id: userData.uid, category: newKwCat, region: newKwRegion, ...extra }]);
      setNewKwCat('');
      setNewKwRegion('');
      setNewKwArea('');
      setNewKwDateFrom('');
      setNewKwPriceFrom('');
      setNewKwPriceTo('');
    } catch (err) { console.error('keyword add failed', err); }
  };

  const removeKeyword = async (id) => {
    try {
      await removePartnerKeyword(id);
      setKeywords((prev) => prev.filter((k) => k.id !== id));
    } catch (err) { console.error('keyword remove failed', err); }
  };

  const sorted = useMemo(() => {
    const filtered = dbs.filter((d) => {
      if (filters.category && d.category !== filters.category) return false;
      if (filters.region && !(d.region || '').includes(filters.region)) return false;
      if (filters.areaSize && d.areaSize !== filters.areaSize) return false;
      if (filters.movingType && d.movingType !== filters.movingType && d.movingTypeValue !== filters.movingType) return false;
      if (filters.internetType && d.internetType !== filters.internetType) return false;
      if (filters.movingDates.length > 0 && !filters.movingDates.includes(d.movingDate)) return false;
      if (filters.search) {
        const kw = filters.search.trim().toLowerCase();
        const cat = PARTNER_SIGNUP_CATEGORIES.find((c) => c.id === d.category)?.label || '';
        const area = AREA_SIZES[d.areaSize]?.label || '';
        const movingLabel = MOVING_TYPES.find((m) => m.value === (d.movingType || d.movingTypeValue))?.label || d.movingType || '';
        const internetLabel = INTERNET_SERVICE_TYPES.find((m) => m.key === d.internetType)?.label || '';
        const hay = [d.region, d.region2, cat, area, movingLabel, internetLabel].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      return true;
    });
    const dateMs = (d) => {
      if (!d?.movingDate) return Number.MAX_SAFE_INTEGER;
      const t = new Date(d.movingDate).getTime();
      return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
    };
    return filtered.sort((a, b) => {
      // 긴급 우선
      if (a.urgent !== b.urgent) return b.urgent ? 1 : -1;
      // 정렬 모드
      if (sort === 'urgent') return dateMs(a) - dateMs(b); // 가까운 일자 먼저
      return dateMs(a) - dateMs(b);
    });
  }, [dbs, filters, sort]);

  const urgentList = sorted.filter((d) => d.urgent);
  const normalList = sorted.filter((d) => !d.urgent);

  return (
    <>
      {/* 관심 키워드 관리 — 업종 2개 이상일 때만 노출 */}
      {showKeywordSection && (
      <Section>
        <SectionHeader onClick={() => setKeywordOpen((v) => !v)}>
          <SectionTitle>
            <IoLocationOutline size={16} />
            관심 키워드 관리
            <InfoTooltip
              align="left"
              text={'관심 지역이나 상품 키워드를 등록해두면 해당 조건에 맞는 신규 DB가 등록될 때 알림을 받을 수 있어요.'}
            />
          </SectionTitle>
          {keywordOpen ? <IoChevronUpOutline size={18} /> : <IoChevronDownOutline size={18} />}
        </SectionHeader>
        {keywordOpen && (
          <SectionBody>
            {keywords.length > 0 && (
              <KeywordGrouped>
                {PARTNER_SIGNUP_CATEGORIES.map((cat) => {
                  const catKeywords = keywords.filter((k) => k.category === cat.id);
                  if (catKeywords.length === 0) return null;
                  return (
                    <KeywordGroup key={cat.id}>
                      <KeywordGroupTitle>{cat.label} <KeywordGroupCount>({catKeywords.length})</KeywordGroupCount></KeywordGroupTitle>
                      <KeywordList>
                        {catKeywords.map((kw) => (
                          <KeywordTag key={kw.id}>
                            {kw.region || '전체 지역'}
                            <KeywordRemove onClick={() => removeKeyword(kw.id)}>
                              <IoCloseOutline size={14} />
                            </KeywordRemove>
                          </KeywordTag>
                        ))}
                      </KeywordList>
                    </KeywordGroup>
                  );
                })}
              </KeywordGrouped>
            )}
            <FormRow>
              <Select value={newKwCat} onChange={(e) => setNewKwCat(e.target.value)}>
                <option value="">카테고리 선택 *</option>
                {PARTNER_SIGNUP_CATEGORIES
                  .filter((c) => myCategories.includes(c.id))
                  .map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
              </Select>
              <Select value={newKwRegion} onChange={(e) => setNewKwRegion(e.target.value)}>
                <option value="">지역 선택 *</option>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </Select>
            </FormRow>
            <FormRow>
              <Select value={newKwArea} onChange={(e) => setNewKwArea(e.target.value)}>
                <option value="">평수(선택)</option>
                {Object.entries(AREA_SIZES).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </Select>
              <DateInput type="date" value={newKwDateFrom} onChange={(e) => setNewKwDateFrom(e.target.value)} />
            </FormRow>
            <FormRow>
              <PriceInput type="number" placeholder="가격 최소(원)" value={newKwPriceFrom} onChange={(e) => setNewKwPriceFrom(e.target.value)} />
              <DateSep>~</DateSep>
              <PriceInput type="number" placeholder="가격 최대(원)" value={newKwPriceTo} onChange={(e) => setNewKwPriceTo(e.target.value)} />
            </FormRow>
            <KeywordHint>
              키워드를 한번에 여러개 입력하면 포함된 키워드 등록완료 알림이 적용됩니다.<br />
              (ex : 경기 + 시흥 = 경기도 시흥시 만 표시됨 / 경기도 X)<br />
              * 키워드는 최대 2개까지 등록 가능합니다.
            </KeywordHint>
            {keywords.length < 2 ? (
              <KeywordSubmit type="button" onClick={addKeyword}>관심 키워드 등록 ({keywords.length}/2)</KeywordSubmit>
            ) : (
              <KeywordHint style={{ textAlign: 'center' }}>키워드 슬롯이 가득 찼습니다. 기존 키워드를 삭제 후 추가하세요.</KeywordHint>
            )}
          </SectionBody>
        )}
      </Section>
      )}

      {/* 필터 / 정렬 */}
      <Section>
        <SectionHeader onClick={() => setFilterOpen((v) => !v)}>
          <SectionTitle>
            <IoCalendarOutline size={16} />
            필터 / 정렬
          </SectionTitle>
          {filterOpen ? <IoChevronUpOutline size={18} /> : <IoChevronDownOutline size={18} />}
        </SectionHeader>
        {filterOpen && (
          <SectionBody>
            <SortChipRow>
              {SORT_CHIPS.map((c) => (
                <SortChip
                  key={c.value}
                  type="button"
                  $active={sort === c.value}
                  onClick={() => setSort(c.value)}
                >
                  {c.value === 'urgent' && '⚡ '}
                  {c.label}
                </SortChip>
              ))}
            </SortChipRow>
            <FormLabel>업종 선택</FormLabel>
            <ChipRow>
              <SortChip type="button" $active={!filters.category} onClick={() => setFilters((p) => ({ ...p, category: '', areaSize: '', movingType: '', internetType: '' }))}>
                전체
              </SortChip>
              {PARTNER_SIGNUP_CATEGORIES
                .filter((c) => myCategories.includes(c.id))
                .map((c) => (
                <SortChip
                  key={c.id}
                  type="button"
                  $active={filters.category === c.id}
                  onClick={() => setFilters((p) => ({ ...p, category: c.id, areaSize: '', movingType: '', internetType: '' }))}
                >
                  {c.label}
                </SortChip>
              ))}
            </ChipRow>
            <FormRow>
              <Select value={filters.region} onChange={setFilter('region')}>
                <option value="">전체 지역</option>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </Select>
              <Spacer />
            </FormRow>

            {hasSubFilters && (
              <>
                <FormLabel>평형</FormLabel>
                <ChipRow>
                  <SortChip type="button" $active={!filters.areaSize} onClick={() => setFilters((p) => ({ ...p, areaSize: '' }))}>전체</SortChip>
                  {Object.entries(AREA_SIZES).map(([k, v]) => (
                    <SortChip
                      key={k}
                      type="button"
                      $active={filters.areaSize === k}
                      onClick={() => setFilters((p) => ({ ...p, areaSize: k }))}
                    >
                      {v.label}
                    </SortChip>
                  ))}
                </ChipRow>
              </>
            )}

            {filters.category === 'moving' && (
              <>
                <FormLabel>이사타입</FormLabel>
                <ChipRow>
                  <SortChip type="button" $active={!filters.movingType} onClick={() => setFilters((p) => ({ ...p, movingType: '' }))}>전체</SortChip>
                  {MOVING_TYPES.map((m) => (
                    <SortChip
                      key={m.value}
                      type="button"
                      $active={filters.movingType === m.value}
                      onClick={() => setFilters((p) => ({ ...p, movingType: m.value }))}
                    >
                      {m.label}
                    </SortChip>
                  ))}
                </ChipRow>
              </>
            )}

            {filters.category === 'internet_tv' && (
              <>
                <FormLabel>인터넷 종류</FormLabel>
                <ChipRow>
                  <SortChip type="button" $active={!filters.internetType} onClick={() => setFilters((p) => ({ ...p, internetType: '' }))}>전체</SortChip>
                  {INTERNET_SERVICE_TYPES.map((m) => (
                    <SortChip
                      key={m.key}
                      type="button"
                      $active={filters.internetType === m.key}
                      onClick={() => setFilters((p) => ({ ...p, internetType: m.key }))}
                    >
                      {m.label}
                    </SortChip>
                  ))}
                </ChipRow>
              </>
            )}

            {filters.category !== 'internet_tv' && (
              <>
                <FormLabel>이사날짜 (다중 선택)</FormLabel>
                <ChipRow>
                  {dateChips.map((d) => (
                    <SortChip
                      key={d.iso}
                      type="button"
                      $active={filters.movingDates.includes(d.iso)}
                      onClick={() => toggleMovingDate(d.iso)}
                    >
                      {d.label}
                    </SortChip>
                  ))}
                </ChipRow>
              </>
            )}

            {hasProductFilter && null}

            <FormRow style={{ marginTop: 8 }}>
              <ResetBtn type="button" onClick={resetFilters}>필터 초기화</ResetBtn>
            </FormRow>
          </SectionBody>
        )}
      </Section>

      {/* 긴급 리스트 */}
      {urgentList.length > 0 && (
        <ListSection>
          <ListSectionTitle>
            긴급 ({urgentList.length}건 남음)
            <InfoTooltip
              align="left"
              text={'15일 이내 이사 예정 고객 리스트가 긴급으로 표시됩니다.\n나머지는 이사가 가까운 일자 순으로 정렬됩니다.\n원하시는 고객을 선택하고 열람하세요.\n열람비용이 결제되며 추가 수수료가 부과됩니다.'}
            />
            <UrgentSelectAll>
              <input
                type="checkbox"
                checked={urgentList.length > 0 && urgentList.every((d) => selected.includes(d.id))}
                onChange={(e) => {
                  const urgentIds = urgentList.map((d) => d.id);
                  if (e.target.checked) {
                    setSelected((prev) => Array.from(new Set([...prev, ...urgentIds])));
                  } else {
                    setSelected((prev) => prev.filter((id) => !urgentIds.includes(id)));
                  }
                }}
              />
              <span>긴급 전체 선택</span>
            </UrgentSelectAll>
          </ListSectionTitle>
          <CardList>
            {urgentList.map((d) => {
              const isFree = !d.price || Number(d.price) === 0;
              return (
                <DbCard
                  key={d.id}
                  data={d}
                  checked={selected.includes(d.id)}
                  onToggle={() => toggleSelect(d.id)}
                  onPurchase={() => isFree ? setFreeTarget(d) : setPurchaseTarget(d)}
                  onFeeClick={() => setFeeInfoCat(d.category)}
                  $urgent
                />
              );
            })}
          </CardList>
        </ListSection>
      )}

      {/* 일반 리스트 */}
      {normalList.length > 0 && (
        <ListSection>
          <ListSectionTitle>그 외 ({normalList.length}건)</ListSectionTitle>
          <CardList>
            {normalList.map((d) => {
              const isFree = !d.price || Number(d.price) === 0;
              return (
                <DbCard
                  key={d.id}
                  data={d}
                  checked={selected.includes(d.id)}
                  onToggle={() => toggleSelect(d.id)}
                  onPurchase={() => isFree ? setFreeTarget(d) : setPurchaseTarget(d)}
                  onFeeClick={() => setFeeInfoCat(d.category)}
                />
              );
            })}
          </CardList>
        </ListSection>
      )}

      {sorted.length === 0 && <EmptyText>조건에 맞는 DB가 없습니다.</EmptyText>}

      {/* 영업 수수료 안내 모달 — 카드 카테고리별 */}
      {feeInfoCat && <FeeInfoModal category={feeInfoCat} onClose={() => setFeeInfoCat(null)} />}

      {/* 구매 결제 모달 */}
      {purchaseTarget && (
        <PurchaseModal
          data={purchaseTarget}
          partnerId={userData?.uid}
          onClose={() => { setPurchaseTarget(null); load(); }}
        />
      )}

      {/* 무료/긴급 즉시 할당 모달 */}
      {freeTarget && !freeDone && (
        <ModalOverlay onClick={() => setFreeTarget(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>무료 DB 즉시 할당</ModalTitle>
              <ModalClose type="button" onClick={() => setFreeTarget(null)}>
                <IoCloseOutline size={22} />
              </ModalClose>
            </ModalHeader>
            <DoneDesc style={{ whiteSpace: 'normal', marginBottom: 16 }}>
              이 DB는 무료/긴급 DB입니다.<br />결제 없이 즉시 내 고객으로 할당됩니다.
            </DoneDesc>
            <ModalBtnRow>
              <ModalBtn type="button" $secondary onClick={() => setFreeTarget(null)}>취소</ModalBtn>
              <ModalBtn
                type="button"
                onClick={async () => {
                  try {
                    if (userData?.uid && freeTarget.id) {
                      await purchaseDB(freeTarget.id, userData.uid, { price: 0, method: '무료할당' });
                    }
                  } catch (err) { console.error(err); }
                  setFreeDone(true);
                }}
              >결제 없이 즉시 할당</ModalBtn>
            </ModalBtnRow>
          </ModalBox>
        </ModalOverlay>
      )}
      {freeTarget && freeDone && (
        <ModalOverlay onClick={() => { setFreeTarget(null); setFreeDone(false); load(); }}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <DoneBox>
              <DoneIconBox><IoInformationCircleOutline size={40} /></DoneIconBox>
              <DoneTitle>무료 DB가 할당되었습니다</DoneTitle>
              <DoneDesc>내 고객 탭에서 확인하실 수 있습니다.</DoneDesc>
              <ModalBtn type="button" onClick={() => { setFreeTarget(null); setFreeDone(false); load(); }}>확인</ModalBtn>
            </DoneBox>
          </ModalBox>
        </ModalOverlay>
      )}

      {sorted.length > 0 && (
        <BottomBulkBar>
          <BottomBulkInner>
            <BottomBulkBtn
              type="button"
              onClick={() => {
                const ids = sorted.map((d) => d.id);
                const allSelected = ids.every((id) => selected.includes(id));
                setSelected(allSelected ? [] : ids);
              }}
            >
              {sorted.length > 0 && sorted.every((d) => selected.includes(d.id)) ? '전체 해제' : '전체 선택'}
            </BottomBulkBtn>
            <BottomBulkBtn
              type="button"
              $primary
              disabled={selected.length === 0}
              onClick={async () => {
                if (!userData?.uid) { setAlertMsg('로그인이 필요합니다.'); return; }
                const targets = sorted.filter((d) => selected.includes(d.id));
                if (targets.length === 0) return;
                const paidTargets = targets.filter((t) => Number(t.price) > 0);
                const freeTargets = targets.filter((t) => !t.price || Number(t.price) === 0);
                const totalAmount = paidTargets.reduce((s, t) => s + Number(t.price || 0), 0);

                if (paidTargets.length === 0) {
                  // 무료건만 — 결제창 없이 즉시 처리
                  if (!window.confirm(`선택한 ${targets.length}건을 즉시 구매하시겠습니까?`)) return;
                  for (const t of freeTargets) {
                    try {
                      await purchaseDB(t.id, userData.uid, { price: 0, method: '무료' });
                    } catch (err) { console.error(err); }
                  }
                  setSelected([]);
                  load();
                  return;
                }

                // 유료 포함 — 합산 금액으로 토스 결제창 1회
                const orderId = buildOrderId('dbb');
                localStorage.setItem(`order:${orderId}`, JSON.stringify({
                  kind: 'db_purchase_bulk',
                  srIds: paidTargets.map((t) => t.id),
                  freeSrIds: freeTargets.map((t) => t.id),
                  partnerUid: userData.uid,
                  prices: paidTargets.reduce((acc, t) => { acc[t.id] = Number(t.price || 0); return acc; }, {}),
                  amount: totalAmount,
                }));
                try {
                  await requestTossPayment({
                    amount: totalAmount,
                    orderId,
                    orderName: `DB 일괄 구매 (${paidTargets.length}건)`,
                    customerName: userData.business_name || userData.name || '제휴업체',
                    method: '카드',
                  });
                } catch (err) {
                  localStorage.removeItem(`order:${orderId}`);
                  setAlertMsg(err?.message || '결제창 호출 실패');
                }
              }}
            >
              {selected.length === 0
                ? '전체 구매'
                : (sorted.length > 0 && sorted.every((d) => selected.includes(d.id))
                    ? `${selected.length}건 전체 구매`
                    : `${selected.length}건 구매`)}
            </BottomBulkBtn>
          </BottomBulkInner>
        </BottomBulkBar>
      )}
      {alertMsg && (
        <AlertModal
          type="error"
          title="안내"
          message={alertMsg}
          onConfirm={() => setAlertMsg('')}
        />
      )}
    </>
  );
};

export default MarketTab;

// ───── 카드 컴포넌트 ─────
const DbCard = ({ data, checked, onToggle, onPurchase, onFeeClick, $urgent }) => {
  const cat = PARTNER_SIGNUP_CATEGORIES.find((c) => c.id === data.category);
  const isFree = !data.price || Number(data.price) === 0;
  let ddayText = '';
  if (data.movingDate) {
    try {
      const md = new Date(data.movingDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diff = Math.ceil((md.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      ddayText = diff < 0 ? `D+${-diff}` : diff === 0 ? 'D-Day' : `D-${diff}`;
    } catch { /* skip */ }
  }
  const regDateText = data.createdAt || data.regDate || '-';
  return (
    <Card $urgent={$urgent}>
      <CardTopRow>
        <CardCheckRow>
          <Checkbox type="checkbox" checked={checked} onChange={onToggle} />
          <CategoryBadge>{cat?.label || data.category}</CategoryBadge>
          {$urgent && <UrgentBadge>긴급</UrgentBadge>}
          {data.badge && !$urgent && <NormalBadge>{data.badge}</NormalBadge>}
          {ddayText && <DDayBadge $urgent={$urgent}>{ddayText}</DDayBadge>}
        </CardCheckRow>
        <RegDate>서비스 신청일<br />{regDateText}</RegDate>
      </CardTopRow>

      <CardInfoBlock>
        <InfoLabel>서비스 예정 지역</InfoLabel>
        <InfoValue>{data.region}{data.region2 ? ` · ${data.region2}` : ''}</InfoValue>
      </CardInfoBlock>

      <CardInfoRow>
        <CardInfoBlock>
          <InfoLabel>평수</InfoLabel>
          <InfoValue>{AREA_SIZES[data.areaSize]?.label || data.areaSize || '-'}</InfoValue>
        </CardInfoBlock>
        <CardInfoBlock>
          <InfoLabel>상품명</InfoLabel>
          <InfoValue>
            {(() => {
              // 카테고리별 분기 — 다른 카테고리 필드가 fallback 매칭되어 잘못 표시되는 문제 방지
              const c = data.category;
              if (c === 'moving') {
                const mv = MOVING_TYPES.find((m) => m.value === (data.movingTypeValue || data.movingType));
                return mv ? mv.label : (data.movingType || cat?.label || '-');
              }
              if (c === 'internet_tv') {
                const it = INTERNET_SERVICE_TYPES.find((m) => m.key === data.internetType);
                return it ? it.label : (cat?.label || '-');
              }
              if (c === 'cleaning') {
                const ct = CLEANING_TYPES.find((m) => m.key === data.cleaningType);
                return ct ? ct.label : (cat?.label || '-');
              }
              if (c === 'interior') {
                const ist = INTERIOR_STYLES.find((m) => m.key === data.interiorStyle);
                return ist ? ist.label : (cat?.label || '-');
              }
              return data.productName || cat?.label || '-';
            })()}
          </InfoValue>
        </CardInfoBlock>
      </CardInfoRow>

      <MoveDateRow>
        <IoCalendarOutline size={14} />
        <MoveDate>서비스 예정일 {(() => {
          const md = data.movingDate;
          if (!md) return '-';
          try {
            const d = new Date(md);
            if (!isNaN(d.getTime())) {
              return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            }
          } catch { /* skip */ }
          return md;
        })()}</MoveDate>
      </MoveDateRow>

      <SellerInfo>
        <IoHomeOutline size={12} />
        <span>구매 후 고객 상세/연락처 확인</span>
        <IoCallOutline size={12} />
        <span>{data.phoneMask || '010-****-****'}</span>
      </SellerInfo>

      <PurchaseBtn type="button" $urgent={$urgent} onClick={onPurchase}>
        {isFree ? '결제 없이 즉시 할당' : '구매하기'}
      </PurchaseBtn>

      <FeeNote type="button" onClick={onFeeClick}>
        <IoInformationCircleOutline size={12} />
        영업 수수료 안내
      </FeeNote>
    </Card>
  );
};

// ───── 구매 결제 모달 (2단계: 결제 → 대기 → 완료) ─────
const PurchaseModal = ({ data, partnerId, onClose }) => {
  const [method, setMethod] = useState('transfer');
  const [phase, setPhase] = useState('form'); // form | pending | done
  const [agreed, setAgreed] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');
  const cat = PARTNER_SIGNUP_CATEGORIES.find((c) => c.id === data.category);

  const handleConfirm = async () => {
    if (!agreed) {
      setAlertMsg('영업 수수료 정책에 동의해 주세요.');
      return;
    }
    // 24시간 이상 미수금이 있으면 DB 구매 차단
    try {
      const overdue = await hasOverdueReceivable(partnerId);
      if (overdue.overdue) {
        setAlertMsg('24시간 이상 결제되지 않은 미수금이 있어 DB 구매가 제한됩니다.\n결제 후 다시 이용해 주세요.');
        return;
      }
    } catch (err) {
      console.error('미수금 검사 실패:', err);
      // 검사 실패는 구매 막지 않음 (서비스 가용성 우선)
    }
    const amount = Number(data.price || 10000);
    const orderId = buildOrderId('db');
    // 결제 후 success 페이지에서 회수할 메타 저장
    localStorage.setItem(`order:${orderId}`, JSON.stringify({
      srId: data.id,
      partnerUid: partnerId,
      amount,
    }));
    const tossMethod = method === 'card' ? '카드'
      : method === 'transfer' ? '계좌이체'
      : method === 'phone' ? '휴대폰'
      : '카드';
    try {
      await requestTossPayment({
        amount,
        orderId,
        orderName: `${cat?.label || 'DB'} 구매`,
        customerName: '제휴업체',
        method: tossMethod,
      });
      // requestPayment는 리다이렉트로 successUrl 이동 — 여기 도달 안 함
    } catch (err) {
      localStorage.removeItem(`order:${orderId}`);
      setAlertMsg(err?.message || '결제창 호출 실패');
    }
  };

  if (phase === 'pending') {
    return (
      <ModalOverlay>
        <ModalBox>
          <PendingBox>
            <Spinner />
            <PendingTitle>결제 확인 대기 중...</PendingTitle>
            <PendingDesc>
              본사에서 결제 내역을 확인하는 중입니다.<br />
              잠시만 기다려 주세요.
            </PendingDesc>
          </PendingBox>
        </ModalBox>
      </ModalOverlay>
    );
  }

  if (phase === 'done') {
    return (
      <ModalOverlay onClick={onClose}>
        <ModalBox onClick={(e) => e.stopPropagation()}>
          <DoneBox>
            <DoneIconBox>
              <IoInformationCircleOutline size={40} />
            </DoneIconBox>
            <DoneTitle>구매가 완료되었습니다</DoneTitle>
            <DoneDesc>
              구매하신 DB는 [내 고객] 탭에서 확인하실 수 있습니다.
            </DoneDesc>
            <ModalBtn type="button" onClick={onClose}>확인</ModalBtn>
          </DoneBox>
        </ModalBox>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>DB 구매 결제</ModalTitle>
          <ModalClose type="button" onClick={onClose}>
            <IoCloseOutline size={22} />
          </ModalClose>
        </ModalHeader>

        <ModalCategory>{cat?.label || data.category} DB</ModalCategory>

        <PriceRow>
          <PriceLabel>DB 영업가</PriceLabel>
          <PriceValue>{fmt(data.price)}</PriceValue>
        </PriceRow>
        <PriceRow $highlight>
          <PriceLabel>실결제 금액</PriceLabel>
          <PriceValueHi>{fmt(data.price)}</PriceValueHi>
        </PriceRow>

        <MethodLabel>결제 수단 <RequiredStar>*</RequiredStar></MethodLabel>
        <MethodGrid>
          {PAYMENT_METHODS.map((m) => (
            <MethodBtn
              key={m.value}
              type="button"
              $active={method === m.value}
              $disabled={m.disabled}
              disabled={m.disabled}
              onClick={() => !m.disabled && setMethod(m.value)}
            >
              <span>{m.label}</span>
              {m.badge && <MethodBadge>{m.badge}</MethodBadge>}
            </MethodBtn>
          ))}
        </MethodGrid>

        <ModalNote>
          <IoInformationCircleOutline size={12} />
          결제 방법은 선택 기록으로 남으며, 실제 결제 본사에서 별도로 처리됩니다.
        </ModalNote>

        <AgreeRow>
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span>구매 시 서비스 완료 기준 영업 수수료가 부과되는 정책에 동의합니다.</span>
        </AgreeRow>

        <ModalBtnRow>
          <ModalBtn type="button" $secondary onClick={onClose}>취소</ModalBtn>
          <ModalBtn type="button" onClick={handleConfirm}>구매 확정</ModalBtn>
        </ModalBtnRow>
      </ModalBox>
      {alertMsg && (
        <AlertModal
          type="error"
          title="안내"
          message={alertMsg}
          onConfirm={() => setAlertMsg('')}
        />
      )}
    </ModalOverlay>
  );
};

// ───── 스타일 ─────
const Section = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1.5px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  overflow: hidden;
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.base};
  background: transparent;
  border: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 0 ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.base};
`;

const FormRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  min-width: 0;

  & > * { min-width: 0; }
`;

const FormLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const Select = styled.select`
  flex: 1;
  padding: 11px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  background: ${({ theme }) => theme.colors.bgWhite};
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  min-height: 42px;

  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const DateInput = styled.input`
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: 11px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  outline: none;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.text};
  min-height: 42px;
`;

const PriceInput = styled.input`
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: 11px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  outline: none;
  box-sizing: border-box;
  min-height: 42px;
`;

const DateSep = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textLight};
`;

const Spacer = styled.div`
  flex: 1;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SortChipRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar { display: none; }
`;

const SortChip = styled.button`
  flex-shrink: 0;
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : '#fff')};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.textSecondary)};
`;

const KeywordGrouped = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const KeywordGroup = styled.div`
  padding: 10px 12px;
  background: #F9FAFB;
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const KeywordGroupTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 6px;
`;

const KeywordGroupCount = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 500;
`;

const KeywordList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const KeywordTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px 4px 12px;
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const KeywordRemove = styled.button`
  display: flex;
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  padding: 0;
`;

const KeywordSubmit = styled.button`
  width: 100%;
  padding: 12px;
  background: #8B5CF6;
  color: #fff;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;

  &:hover { background: #7C3AED; }
`;

// 리스트
const ListSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ListSectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UrgentSelectAll = styled.label`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #6B7280;
  cursor: pointer;
  input { cursor: pointer; }
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base};
  border: ${({ $urgent, theme }) => ($urgent ? '1.5px solid #EF4444' : `1.5px solid ${theme.colors.border}`)};
  box-shadow: ${({ $urgent }) => ($urgent ? '0 0 0 3px rgba(239, 68, 68, 0.08)' : 'none')};
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const CardCheckRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  flex-wrap: wrap;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const CategoryBadge = styled.span`
  padding: 3px 10px;
  background: ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.text};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const UrgentBadge = styled.span`
  padding: 3px 10px;
  background: #EF4444;
  color: #fff;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  letter-spacing: -0.2px;
`;

const NormalBadge = styled.span`
  padding: 3px 10px;
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const RegDate = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textLight};
  text-align: right;
  line-height: 1.4;
`;

const CardInfoBlock = styled.div`
  flex: 1;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const CardInfoRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const InfoLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: 2px;
`;

const InfoValue = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const MoveDateRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const MoveDate = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const SellerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const PurchaseBtn = styled.button`
  width: 100%;
  padding: 12px;
  background: ${({ $urgent, theme }) => ($urgent ? '#EF4444' : theme.colors.primary)};
  color: #fff;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;

  &:hover { opacity: 0.95; }
`;

const FeeNote = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.sm};
  padding: 4px 0;
  background: none;
  border: 0;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
`;

const EmptyText = styled.p`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

// 모달
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 360px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ModalTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const ModalClose = styled.button`
  display: flex;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 0;
`;

const ModalCategory = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const PriceLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PriceValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const PriceValueHi = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const MethodLabel = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const RequiredStar = styled.span`
  color: ${({ theme }) => theme.colors.danger};
`;

const MethodHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  margin: 4px 0 ${({ theme }) => theme.spacing.sm};
`;

const MethodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const MethodBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 8px;
  border: 1.5px solid ${({ $active, $disabled, theme }) =>
    $disabled ? '#E5E7EB'
    : $active ? theme.colors.primary
    : theme.colors.border};
  background: ${({ $active, $disabled, theme }) =>
    $disabled ? '#F3F4F6'
    : $active ? `${theme.colors.primary}10`
    : '#fff'};
  color: ${({ $active, $disabled, theme }) =>
    $disabled ? '#9CA3AF'
    : $active ? theme.colors.primary
    : theme.colors.text};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`;

const MethodBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  background: #E5E7EB;
  color: #6B7280;
  padding: 2px 5px;
  border-radius: 3px;
`;

const ModalNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  line-height: 1.5;
`;

const ModalBtnRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ModalBtn = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;
  border: ${({ $secondary, theme }) => ($secondary ? `1px solid ${theme.colors.border}` : '0')};
  background: ${({ $secondary, theme }) => ($secondary ? '#fff' : theme.colors.primary)};
  color: ${({ $secondary, theme }) => ($secondary ? theme.colors.text : '#fff')};
`;

// 2단계 모달 — 로딩
const PendingBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.lg};
  text-align: center;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid ${({ theme }) => theme.colors.borderLight};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const PendingTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const PendingDesc = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin: 0;
`;

// 2단계 모달 — 완료
const DoneBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
`;

const DoneIconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const DoneTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const DoneDesc = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
  white-space: nowrap;
`;

// ── 영업 수수료 안내 모달 (간이 — MyCustomerTab 상세 모달 재사용 권장)
const FEE_INFO = {
  moving: {
    title: '이사 영업 수수료',
    summary: '12평 기준 산출액: 포장이사 (평당 4,000원) = 48,000원',
    rows: [
      { name: '일반이사', fee: 1500 },
      { name: '반포장이사', fee: 3000 },
      { name: '포장이사', fee: 4000 },
      { name: '보관이사', fee: 4000 },
    ],
    unit: '면적당',
  },
  cleaning: {
    title: '청소 영업 수수료',
    summary: '12평 기준 산출액: 입주청소 (평당 3,000원) = 36,000원',
    rows: [
      { name: '일반청소', fee: 2000 },
      { name: '입주청소', fee: 3000 },
      { name: '특수청소', fee: 4500 },
    ],
    unit: '면적당',
  },
  internet_tv: {
    title: '인터넷 영업 수수료',
    summary: '설치 전용 산출액: 인터넷 단독 설치 = 15,000원',
    rows: [
      { name: '인터넷 단독', fee: 15000 },
      { name: 'TV + 인터넷', fee: 20000 },
      { name: '결합상품', fee: 25000 },
    ],
    unit: '건당',
  },
  interior: {
    title: '인테리어 영업 수수료',
    summary: '12평 기준 산출액: 도배 (평당 5,000원) = 60,000원',
    rows: [
      { name: '도배', fee: 5000 },
      { name: '장판', fee: 4000 },
      { name: '도배+장판', fee: 8000 },
    ],
    unit: '면적당',
  },
};

const DDayBadge = styled.span`
  padding: 3px 8px;
  background: ${({ $urgent }) => ($urgent ? '#FEE2E2' : '#EEF2FF')};
  color: ${({ $urgent }) => ($urgent ? '#DC2626' : '#4A6CF7')};
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
`;

const KeywordHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.5;
  margin-top: 2px;
`;

const ResetBtn = styled.button`
  flex: 1;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;

  &:hover { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const BulkRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const BottomBulkBar = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 20;
  margin: 16px -16px 0;
  padding: 12px 16px 16px;
  background: #fff;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.04);

  @media (min-width: 1024px) {
    margin: 16px 0 0;
    border-radius: 0 0 12px 12px;
  }
`;

const BottomBulkInner = styled.div`
  display: flex;
  gap: 8px;
  max-width: 720px;
  margin: 0 auto;
`;

const BottomBulkBtn = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;
  border: ${({ $primary, theme }) => ($primary ? '0' : `1px solid ${theme.colors.border}`)};
  background: ${({ $primary, theme }) => ($primary ? theme.colors.primary : '#fff')};
  color: ${({ $primary, theme }) => ($primary ? '#fff' : theme.colors.text)};
  transition: opacity 0.15s ease;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    opacity: 0.9;
  }
`;

const BulkBtn = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;
  border: ${({ $primary, theme }) => ($primary ? '0' : `1px solid ${theme.colors.border}`)};
  background: ${({ $primary }) => ($primary ? '#EF4444' : '#fff')};
  color: ${({ $primary, theme }) => ($primary ? '#fff' : theme.colors.text)};

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const AgreeRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 12px;
  line-height: 1.5;
  cursor: pointer;

  input { margin-top: 3px; accent-color: ${({ theme }) => theme.colors.primary}; }
`;

const FeeInfoModal = ({ category, onClose }) => {
  const info = FEE_INFO[category] || FEE_INFO.moving;
  return (
    <ModalOverlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{info.title}</ModalTitle>
          <ModalClose type="button" onClick={onClose}>
            <IoCloseOutline size={22} />
          </ModalClose>
        </ModalHeader>
        <div style={{ background: '#EFF6FF', borderRadius: 8, padding: 12, fontSize: 13, fontWeight: 600, color: '#4A6CF7', margin: '8px 0 12px' }}>
          {info.summary}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
          <thead>
            <tr>
              <th style={{ padding: '10px 8px', fontSize: 12, background: '#F3F4F6', textAlign: 'left' }}>서비스 명</th>
              <th style={{ padding: '10px 8px', fontSize: 12, background: '#F3F4F6', textAlign: 'right' }}>{info.unit} 수수료(원)</th>
            </tr>
          </thead>
          <tbody>
            {info.rows.map((r) => (
              <tr key={r.name}>
                <td style={{ padding: '10px 8px', fontSize: 13, borderBottom: '1px solid #F3F4F6' }}>{r.name}</td>
                <td style={{ padding: '10px 8px', fontSize: 13, borderBottom: '1px solid #F3F4F6', textAlign: 'right' }}>{r.fee.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8 }}>
          ※ 실제 수수료는 서비스 유형 및 면적에 따라 변동될 수 있습니다.
        </div>
        <ModalBtn type="button" onClick={onClose}>확인</ModalBtn>
      </ModalBox>
    </ModalOverlay>
  );
};
